/**
 * ConnectionCard Component
 * Displays a bank connection with status, accounts, and actions
 * WP-FRONTEND-CONN-1: Enhanced with view details button and consent expiration badge
 */

import { useState, useCallback, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { Card, Button, Alert } from '@/components/ui';
import ConnectionStatus from './ConnectionStatus';
import PlaidLinkButton from './PlaidLinkButton';
import { formatRelativeTime, formatCurrency, formatAccountMask, capitalize } from '@/utils/format';
import { cn } from '@/utils/cn';
import { connectionsService, type ConnectionAccount } from '@/services/connections';
import type { Connection } from '@/types';

export interface ConnectionCardProps {
  /**
   * Connection data
   */
  connection: Connection;
  /**
   * Callback when delete is requested
   */
  onDelete?: (connectionId: string) => void;
  /**
   * Callback when sync is requested
   */
  onSync?: (connectionId: string) => void;
  /**
   * Callback when reconnection succeeds
   */
  onReconnect?: () => void;
  /**
   * Callback when "View Details" is clicked
   * Opens the ConnectionDetailsModal with sync history, accounts, and force refresh
   */
  onViewDetails?: (connectionId: string) => void;
  /**
   * Whether delete is in progress
   */
  isDeleting?: boolean;
  /**
   * Whether sync is in progress
   */
  isSyncing?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Start with accounts expanded
   */
  defaultExpanded?: boolean;
  /**
   * Consent expiration date (from ConnectionDetail)
   * Shows warning badge if expiring soon
   */
  consentExpiresAt?: string | null;
}

/**
 * Institution icon with initials
 * Shows the first letters of the institution name with a colored background
 */
const InstitutionIcon = ({ institutionName }: { institutionName: string }) => {
  // Generate initials from institution name (max 2 characters)
  const getInitials = (name: string): string => {
    if (!name) return '??';
    const words = name.split(/\s+/).filter(Boolean);
    if (words.length === 0) return '??';
    if (words.length === 1) {
      return (words[0] ?? '').slice(0, 2).toUpperCase();
    }
    // Check if first word is an abbreviation (2-3 uppercase letters like "TD", "BMO", "PNC")
    const firstWord = words[0] ?? '';
    if (firstWord.length <= 3 && firstWord === firstWord.toUpperCase() && /^[A-Z]+$/.test(firstWord)) {
      // Use the abbreviation as initials (e.g., "TD Bank" -> "TD")
      return firstWord.slice(0, 2);
    }
    // Otherwise take first letter of each word
    return words
      .slice(0, 2)
      .map((w) => w?.[0] ?? '')
      .join('')
      .toUpperCase();
  };

  // Generate a consistent color based on institution name
  const getColor = (name: string): string => {
    const colors: readonly string[] = [
      'bg-blue-600 text-white',
      'bg-green-600 text-white',
      'bg-purple-600 text-white',
      'bg-rose-600 text-white',
      'bg-amber-600 text-white',
      'bg-cyan-600 text-white',
      'bg-indigo-600 text-white',
      'bg-teal-600 text-white',
    ] as const;
    // Simple hash of the name to pick a color consistently
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length] ?? 'bg-gray-600 text-white';
  };

  return (
    <div
      className={cn(
        'flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl text-lg font-bold',
        getColor(institutionName)
      )}
      aria-hidden="true"
    >
      {getInitials(institutionName)}
    </div>
  );
};

/**
 * Get health status details for display
 */
const getHealthStatus = (
  status: Connection['status'],
  lastSyncAt: string | null
): { icon: 'healthy' | 'warning' | 'error'; label: string; description: string } => {
  if (status === 'error') {
    return {
      icon: 'error',
      label: 'Error',
      description: 'Connection has an error. Please reconnect.',
    };
  }
  if (status === 'pending_reauth') {
    return {
      icon: 'warning',
      label: 'Needs Attention',
      description: 'Re-authentication required.',
    };
  }
  if (status === 'pending') {
    return {
      icon: 'warning',
      label: 'Pending',
      description: 'Connection is being set up.',
    };
  }
  // For active status, check last sync time
  if (lastSyncAt) {
    const lastSync = new Date(lastSyncAt);
    const now = new Date();
    const hoursSinceSync = (now.getTime() - lastSync.getTime()) / (1000 * 60 * 60);
    if (hoursSinceSync > 24) {
      return {
        icon: 'warning',
        label: 'Stale',
        description: 'Data may be outdated. Last sync was over 24 hours ago.',
      };
    }
  }
  return {
    icon: 'healthy',
    label: 'Healthy',
    description: 'Connection is working normally.',
  };
};

/**
 * Calculate days until consent expires
 * Returns null if no expiration, negative/zero if expired, positive if not yet expired
 */
const getDaysUntilConsent = (expiresAt: string | null | undefined): number | null => {
  if (!expiresAt) return null;
  const expDate = new Date(expiresAt);
  const now = new Date();
  const diffMs = expDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return diffDays;
};

/**
 * Small inline consent expiration badge
 * Shows warning when consent expires within 30 days or has expired
 */
const ConsentBadge = ({ expiresAt }: { expiresAt: string | null | undefined }) => {
  const daysUntil = getDaysUntilConsent(expiresAt);

  // Don't show if no expiration or more than 30 days away
  if (daysUntil === null || daysUntil > 30) {
    return null;
  }

  // Check if already expired
  const isExpired = daysUntil <= 0;
  const isUrgent = daysUntil <= 7 && !isExpired;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium',
        isExpired
          ? 'bg-red-100 text-red-800'
          : isUrgent
            ? 'bg-amber-100 text-amber-800'
            : 'bg-yellow-100 text-yellow-800'
      )}
      title={isExpired ? 'Access has expired' : `Access expires in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`}
    >
      <svg
        className="h-3 w-3"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      {isExpired ? 'Expired' : `${daysUntil}d`}
    </span>
  );
};

/**
 * Health status icon component
 */
const HealthIcon = ({ status }: { status: 'healthy' | 'warning' | 'error' }) => {
  const iconConfig = {
    healthy: {
      color: 'text-green-500',
      bgColor: 'bg-green-100',
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 13l4 4L19 7"
        />
      ),
    },
    warning: {
      color: 'text-amber-500',
      bgColor: 'bg-amber-100',
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      ),
    },
    error: {
      color: 'text-red-500',
      bgColor: 'bg-red-100',
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M6 18L18 6M6 6l12 12"
        />
      ),
    },
  };

  const config = iconConfig[status];

  return (
    <div
      className={cn(
        'flex h-6 w-6 items-center justify-center rounded-full',
        config.bgColor
      )}
      aria-hidden="true"
    >
      <svg className={cn('h-4 w-4', config.color)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        {config.icon}
      </svg>
    </div>
  );
};

/**
 * Account type icon
 * Handles both backend account types (depository, credit, loan, investment, other)
 * and subtypes (checking, savings, credit_card, mortgage, etc.)
 */
const AccountTypeIcon = ({ type, subtype }: { type: string; subtype?: string }) => {
  // Use subtype for more specific icons if available
  const effectiveType = subtype?.toLowerCase() || type.toLowerCase();

  const getIcon = () => {
    switch (effectiveType) {
      case 'checking':
      case 'depository':
        return (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
          />
        );
      case 'savings':
      case 'money_market':
      case 'cd':
        return (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
          />
        );
      case 'credit':
      case 'credit_card':
        return (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
          />
        );
      case 'investment':
      case 'brokerage':
      case 'ira':
      case 'roth':
      case '401k':
      case '403b':
        return (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
          />
        );
      case 'loan':
      case 'mortgage':
      case 'home_equity':
      case 'auto':
      case 'student':
      case 'personal':
        return (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        );
      default:
        return (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        );
    }
  };

  const getColors = () => {
    // Map by type first, then use subtype for more specific colors
    const typeKey = type?.toLowerCase() ?? '';
    switch (typeKey) {
      case 'depository':
        // Use subtype for depository accounts
        if (effectiveType === 'savings' || effectiveType === 'money_market' || effectiveType === 'cd') {
          return 'bg-green-100 text-green-600';
        }
        return 'bg-blue-100 text-blue-600'; // checking and default
      case 'credit':
        return 'bg-orange-100 text-orange-600';
      case 'investment':
        return 'bg-purple-100 text-purple-600';
      case 'loan':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div
      className={cn(
        'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full',
        getColors()
      )}
      aria-hidden="true"
    >
      <svg
        className="h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        {getIcon()}
      </svg>
    </div>
  );
};

/**
 * Delete confirmation modal
 */
function DeleteConfirmation({
  institutionName,
  onConfirm,
  onCancel,
  isDeleting,
}: {
  institutionName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-dialog-title"
      >
        <h3 id="delete-dialog-title" className="text-lg font-semibold text-gray-900">
          Remove Connection
        </h3>
        <p className="mt-2 text-sm text-gray-600">
          Are you sure you want to remove your connection to{' '}
          <span className="font-medium">{institutionName}</span>? This will also remove all
          associated accounts and transactions.
        </p>
        <Alert variant="warning" className="mt-4">
          This action cannot be undone.
        </Alert>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} isLoading={isDeleting}>
            {isDeleting ? 'Removing...' : 'Remove Connection'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * ConnectionCard - Displays a bank connection with management options
 *
 * Features:
 * - Prominent institution name display
 * - Expandable account list with individual balances
 * - Health status indicators
 *
 * @example
 * ```tsx
 * <ConnectionCard
 *   connection={connection}
 *   onDelete={(id) => deleteConnection(id)}
 *   onSync={(id) => syncConnection(id)}
 *   defaultExpanded={true}
 * />
 * ```
 */
export default function ConnectionCard({
  connection,
  onDelete,
  onSync,
  onReconnect,
  onViewDetails,
  isDeleting = false,
  isSyncing = false,
  className,
  defaultExpanded = false,
  consentExpiresAt,
}: ConnectionCardProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAccounts, setShowAccounts] = useState(defaultExpanded);
  const [accounts, setAccounts] = useState<ConnectionAccount[]>([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [accountsError, setAccountsError] = useState<string | null>(null);

  const institutionName = connection.institutionName || 'Unknown Institution';
  const healthStatus = getHealthStatus(connection.status, connection.lastSyncAt ?? null);
  const needsReauth = connection.status === 'pending_reauth' || connection.status === 'error';

  const handleViewDetailsClick = useCallback(() => {
    onViewDetails?.(connection.id);
  }, [connection.id, onViewDetails]);

  // Auto-fetch accounts when defaultExpanded is true
  useEffect(() => {
    const fetchAccounts = async () => {
      if (defaultExpanded && accounts.length === 0 && connection.accountsCount > 0) {
        setIsLoadingAccounts(true);
        setAccountsError(null);
        try {
          const fetchedAccounts = await connectionsService.getAccounts(connection.id);
          setAccounts(fetchedAccounts);
        } catch (error) {
          setAccountsError('Failed to load accounts');
          // Log to console in development
          if (import.meta.env.DEV) {
            console.error('Failed to load accounts for connection:', connection.id);
          }
          // Send to Sentry in production (no PII - only connection ID)
          Sentry.captureMessage('Failed to load accounts for connection', {
            level: 'error',
            extra: { connectionId: connection.id, error: String(error) },
          });
        } finally {
          setIsLoadingAccounts(false);
        }
      }
    };
    fetchAccounts();
  }, [defaultExpanded, accounts.length, connection.accountsCount, connection.id]);

  const handleToggleAccounts = useCallback(async () => {
    if (showAccounts) {
      // Collapse
      setShowAccounts(false);
      return;
    }

    // Expand - fetch accounts if not already loaded
    if (accounts.length === 0) {
      setIsLoadingAccounts(true);
      setAccountsError(null);
      try {
        const fetchedAccounts = await connectionsService.getAccounts(connection.id);
        setAccounts(fetchedAccounts);
      } catch (error) {
        setAccountsError('Failed to load accounts');
        // Log to console in development
        if (import.meta.env.DEV) {
          console.error('Failed to load accounts for connection:', connection.id);
        }
        // Send to Sentry in production (no PII - only connection ID)
        Sentry.captureMessage('Failed to load accounts for connection', {
          level: 'error',
          extra: { connectionId: connection.id, error: String(error) },
        });
      } finally {
        setIsLoadingAccounts(false);
      }
    }
    setShowAccounts(true);
  }, [showAccounts, accounts.length, connection.id]);

  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    onDelete?.(connection.id);
    setShowDeleteConfirm(false);
  }, [connection.id, onDelete]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  const handleSyncClick = useCallback(() => {
    onSync?.(connection.id);
  }, [connection.id, onSync]);

  return (
    <>
      <Card className={cn('relative overflow-hidden', className)} padding="none">
        {/* Header section with institution info */}
        <div className="p-4 sm:p-5">
          <div className="flex items-start gap-4">
            {/* Institution icon with initials */}
            <InstitutionIcon institutionName={institutionName} />

            {/* Connection details */}
            <div className="flex-1 min-w-0">
              {/* Institution name - prominent display */}
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="text-lg font-bold text-gray-900 truncate">
                  {institutionName}
                </h3>
                <div className="flex items-center gap-2">
                  <HealthIcon status={healthStatus.icon} />
                  <ConnectionStatus status={connection.status} showText size="sm" />
                  {/* Consent expiration badge - WP-FRONTEND-CONN-1 */}
                  <ConsentBadge expiresAt={consentExpiresAt} />
                </div>
              </div>

              {/* Connection metadata */}
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  {connection.accountsCount} {connection.accountsCount === 1 ? 'account' : 'accounts'}
                </span>
                {connection.lastSyncAt && (
                  <span className="flex items-center gap-1">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Synced {formatRelativeTime(connection.lastSyncAt)}
                  </span>
                )}
              </div>

              {/* Status description for non-healthy connections */}
              {healthStatus.icon !== 'healthy' && (
                <p className="mt-2 text-sm text-gray-600">
                  {healthStatus.description}
                </p>
              )}

              {/* Error details if present */}
              {connection.errorMessage && (
                <Alert variant="error" className="mt-3">
                  {connection.errorMessage}
                </Alert>
              )}

              {/* WP-FE-7: Disconnected status message */}
              {connection.status === 'disconnected' && (
                <Alert variant="warning" className="mt-3">
                  This connection was revoked by your bank. Please delete and add a new connection.
                </Alert>
              )}

              {/* WP-CONN-1: Investment consent upgrade prompt */}
              {connection.needsInvestmentConsent && connection.status !== 'disconnected' && (
                <Alert variant="warning" className="mt-3">
                  <div className="flex flex-col gap-2">
                    <div>
                      <strong>Investment data not syncing</strong>
                      <p className="text-sm mt-1">
                        Reconnect this account to sync investment holdings and positions.
                      </p>
                    </div>
                    <PlaidLinkButton
                      connectionId={connection.id}
                      variant="primary"
                      size="sm"
                      onSuccess={onReconnect}
                    >
                      Reconnect to Sync Investments
                    </PlaidLinkButton>
                  </div>
                </Alert>
              )}
            </div>

            {/* Actions - positioned at top right */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {needsReauth ? (
                <PlaidLinkButton
                  connectionId={connection.id}
                  variant="primary"
                  size="sm"
                  onSuccess={onReconnect}
                >
                  Reconnect
                </PlaidLinkButton>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSyncClick}
                  isLoading={isSyncing}
                  disabled={isSyncing || connection.status === 'pending' || connection.status === 'disconnected'}
                  aria-label={`Sync ${institutionName}`}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  <span className="ml-1 hidden sm:inline">Sync</span>
                </Button>
              )}

              {/* View Details button - WP-FRONTEND-CONN-1 */}
              {onViewDetails && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleViewDetailsClick}
                  aria-label={`View details for ${institutionName}`}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </Button>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={handleDeleteClick}
                disabled={isDeleting}
                aria-label={`Remove ${institutionName}`}
                className="text-gray-400 hover:text-red-600"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </Button>
            </div>
          </div>
        </div>

        {/* Expandable accounts section */}
        {connection.accountsCount > 0 && (
          <div className="border-t border-gray-100">
            {/* Expand/collapse button */}
            <button
              type="button"
              onClick={handleToggleAccounts}
              disabled={isLoadingAccounts}
              className="flex w-full items-center justify-between px-4 sm:px-5 py-3 text-sm text-gray-600 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              aria-expanded={showAccounts}
              aria-controls={`accounts-list-${connection.id}`}
            >
              <span className="flex items-center gap-2">
                <svg
                  className={cn(
                    'h-4 w-4 transition-transform duration-200',
                    showAccounts && 'rotate-90'
                  )}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                {isLoadingAccounts ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Loading accounts...
                  </span>
                ) : (
                  <span className="font-medium">
                    {showAccounts ? 'Hide accounts' : 'View accounts'}
                  </span>
                )}
              </span>
              {!isLoadingAccounts && (
                <span className="text-xs text-gray-400">
                  {connection.accountsCount} {connection.accountsCount === 1 ? 'account' : 'accounts'}
                </span>
              )}
            </button>

            {/* Accounts list */}
            {showAccounts && (
              <div
                id={`accounts-list-${connection.id}`}
                className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-2"
                role="list"
                aria-label="Connected accounts"
              >
                {accountsError && (
                  <Alert variant="error" className="text-sm">
                    {accountsError}
                  </Alert>
                )}

                {accounts.length === 0 && !accountsError && !isLoadingAccounts && (
                  <p className="text-sm text-gray-500 py-2">No accounts found</p>
                )}

                {accounts.map((account) => (
                  <div
                    key={account.id}
                    className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    role="listitem"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <AccountTypeIcon type={account.type} subtype={account.subtype} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {account.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {capitalize(account.subtype || account.type)}
                          {account.mask && (
                            <span className="ml-2 font-mono text-gray-400">
                              {formatAccountMask(account.mask)}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-4">
                      <p
                        className={cn(
                          'text-sm font-semibold',
                          account.balance >= 0 ? 'text-gray-900' : 'text-red-600'
                        )}
                      >
                        {formatCurrency(account.balance, account.currency)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <DeleteConfirmation
          institutionName={connection.institutionName || 'this institution'}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
}
